import axios from 'axios';
import { getApiBaseUrl } from './apiBase';

const API_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // withCredentials: true so the httpOnly refresh_token cookie set by
  // /api/auth/login is sent back to /api/auth/refresh. Bearer token for
  // normal requests still comes from localStorage via the request
  // interceptor below.
  withCredentials: true,
});

// Add axios interceptor to attach Bearer token from localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------
// Silent refresh-on-401 interceptor for admin calls.
// On the first 401 from any admin-protected call, call /api/auth/refresh
// (which reads the httpOnly refresh_token cookie), store the new access
// token in localStorage, and retry the original request once.
// If refresh fails or we are already retrying, clear auth and surface
// the 401 so the UI can send the user back to /admin login.
// ---------------------------------------------------------------------

// Routes that should NEVER trigger a refresh loop.
const AUTH_ENDPOINTS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout'];

let refreshPromise = null;

const performRefresh = async () => {
  // De-duplicate concurrent 401s into a single refresh request.
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        const newToken = res?.data?.access_token;
        if (newToken) {
          localStorage.setItem('access_token', newToken);
        }
        return newToken;
      })
      .finally(() => {
        // Release the lock on next tick so any in-flight 401s can reuse it.
        setTimeout(() => { refreshPromise = null; }, 0);
      });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const url = originalRequest.url || '';
    const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => url.endsWith(p));

    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        const newToken = await performRefresh();
        if (newToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshErr) {
        // Refresh failed — clear stale auth and let the 401 propagate.
        localStorage.removeItem('access_token');
        localStorage.removeItem('admin_user');
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin')) {
          window.location.href = '/admin';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Booking APIs
export const createBooking = async (bookingData) => {
  const response = await api.post('/api/bookings', bookingData);
  return response.data;
};

export const getAllBookings = async () => {
  const response = await api.get('/api/bookings');
  return response.data;
};

export const getBookingById = async (bookingId) => {
  const response = await api.get(`/api/bookings/${bookingId}`);
  return response.data;
};

export const updateBookingStatus = async (bookingId, status) => {
  const response = await api.patch(`/api/bookings/${bookingId}/status`, null, {
    params: { status }
  });
  return response.data;
};

export const updateBookingFare = async (bookingId, actualFare) => {
  const response = await api.patch(`/api/bookings/${bookingId}/fare`, null, {
    params: { actualFare }
  });
  return response.data;
};

// PRODUCTION-CRITICAL TAXI-98 BOOKING NOTIFICATION LOGIC.
// DO NOT MODIFY DURING UNRELATED TASKS.
// target: 'customer' | 'admin' | 'both' (default 'both' for backward compat).
export const resendConfirmationEmail = async (bookingId, target = 'both') => {
  const response = await api.post(`/api/bookings/${bookingId}/resend-confirmation`, null, { params: { target } });
  return response.data;
};

export const resendReceiptEmail = async (bookingId) => {
  const response = await api.post(`/api/bookings/${bookingId}/resend-receipt`);
  return response.data;
};

// Receipt APIs
export const createReceipt = async (receiptData) => {
  const response = await api.post('/api/receipts', receiptData);
  return response.data;
};

export const getAllReceipts = async () => {
  const response = await api.get('/api/receipts');
  return response.data;
};

export const createManualReceipt = async (receiptData) => {
  const response = await api.post('/api/receipts/manual', receiptData);
  return response.data;
};

// Mailing List APIs
export const subscribeToMailingList = async (subscriberData) => {
  const response = await api.post('/api/mailing-list/subscribe', subscriberData);
  return response.data;
};

export const getAllSubscribers = async () => {
  const response = await api.get('/api/mailing-list/subscribers');
  return response.data;
};

export const updateSubscriber = async (subscriberId, updates) => {
  const response = await api.patch(`/api/mailing-list/subscribers/${subscriberId}`, updates);
  return response.data;
};

export const deleteSubscriber = async (subscriberId) => {
  const response = await api.delete(`/api/mailing-list/subscribers/${subscriberId}`);
  return response.data;
};

export const getBookingEmailStatus = async (bookingId) => {
  const response = await api.get(`/api/admin/bookings/${bookingId}/email-status`);
  return response.data;
};

// Receipt APIs
export const generateReceipt = async (bookingId) => {
  const response = await api.post(`/api/bookings/${bookingId}/generate-receipt`);
  return response.data;
};

export const getReceiptByBookingNumber = async (bookingNumber) => {
  const response = await api.get(`/api/receipt/${bookingNumber}`);
  return response.data;
};

export const downloadReceiptPdf = (bookingNumber) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  window.open(`${backendUrl}/api/receipt/${bookingNumber}/download`, '_blank');
};

export const sendReceiptEmail = async (bookingId) => {
  const response = await api.post(`/api/bookings/${bookingId}/send-receipt`);
  return response.data;
};

// Review APIs
export const createReview = async (reviewData) => {
  const response = await api.post('/api/reviews', reviewData);
  return response.data;
};

export const getApprovedReviews = async (limit = 50) => {
  const response = await api.get(`/api/reviews?limit=${limit}`);
  return response.data;
};

export const getPendingReviews = async () => {
  const response = await api.get('/api/reviews/pending');
  return response.data;
};

export const getAllReviews = async () => {
  const response = await api.get('/api/reviews/all');
  return response.data;
};

export const approveReview = async (reviewId) => {
  const response = await api.patch(`/api/reviews/${reviewId}/approve`);
  return response.data;
};

export const rejectReview = async (reviewId) => {
  const response = await api.patch(`/api/reviews/${reviewId}/reject`);
  return response.data;
};

export const respondToReview = async (reviewId, adminResponse) => {
  const response = await api.post(`/api/reviews/${reviewId}/respond`, { adminResponse });
  return response.data;
};

export const getReviewStats = async () => {
  const response = await api.get('/api/reviews/stats');
  return response.data;
};

export const deleteBooking = async (bookingId) => {
  const response = await api.delete(`/api/bookings/${bookingId}`);
  return response.data;
};

// Tracking APIs
export const initializeTracking = async (bookingId) => {
  const response = await api.post(`/api/tracking/${bookingId}/initialize`);
  return response.data;
};

export const updateDriverLocation = async (bookingId, latitude, longitude) => {
  const response = await api.post(`/api/driver/location/${bookingId}`, {
    latitude,
    longitude,
    timestamp: new Date().toISOString()
  });
  return response.data;
};

export const getTrackingData = async (bookingId) => {
  const response = await api.get(`/api/tracking/${bookingId}`);
  return response.data;
};

export default api;



// Manual Ride APIs
export const createManualRide = async (rideData) => {
  const response = await api.post('/api/bookings/manual', rideData);
  return response.data;
};

export const editBooking = async (bookingId, updates) => {
  const response = await api.patch(`/api/bookings/${bookingId}/edit`, updates);
  return response.data;
};

// Fuel Purchase APIs
export const createFuelPurchase = async (fuelData) => {
  const response = await api.post('/api/fuel-purchases', fuelData);
  return response.data;
};

export const getFuelPurchases = async (params) => {
  const response = await api.get('/api/fuel-purchases', { params });
  return response.data;
};

export const editFuelPurchase = async (fuelId, updates) => {
  const response = await api.patch(`/api/fuel-purchases/${fuelId}`, updates);
  return response.data;
};

export const deleteFuelPurchase = async (fuelId) => {
  const response = await api.delete(`/api/fuel-purchases/${fuelId}`);
  return response.data;
};

// Daily Operations APIs
export const getDailyOperations = async (date) => {
  const response = await api.get(`/api/daily-operations/${date}`);
  return response.data;
};

export const getUpcomingRides = async (limit = 10) => {
  const response = await api.get('/api/upcoming-rides', { params: { limit } });
  return response.data;
};


// Complete a booking (admin only)
export const completeBooking = async (bookingId, actualFare, paymentMethod = 'cash', notes = '') => {
  const response = await api.post(`/api/bookings/${bookingId}/complete`, null, {
    params: {
      actual_fare: actualFare,
      payment_method: paymentMethod,
      notes: notes
    }
  });
  return response.data;
};

// Recent bookings since a timestamp (admin only) - used by lightweight notification poller
export const getRecentBookings = async (sinceISO) => {
  const response = await api.get('/api/admin/bookings/recent', {
    params: { since: sinceISO }
  });
  return response.data;
};

// Sun Med Invoice APIs
export const getAllSunMedInvoices = async () => {
  const response = await api.get('/api/invoices/sunmed');
  return response.data;
};

export const getSunMedInvoiceById = async (invoiceId) => {
  const response = await api.get(`/api/invoices/sunmed/${invoiceId}`);
  return response.data;
};

export const createSunMedInvoice = async (invoiceData) => {
  const response = await api.post('/api/invoices/sunmed', invoiceData);
  return response.data;
};

export const updateSunMedInvoice = async (invoiceId, invoiceData) => {
  const response = await api.put(`/api/invoices/sunmed/${invoiceId}`, invoiceData);
  return response.data;
};

export const sendSunMedInvoice = async (invoiceId) => {
  const response = await api.post(`/api/invoices/sunmed/${invoiceId}/send`);
  return response.data;
};

export const resendSunMedInvoice = async (invoiceId) => {
  const response = await api.post(`/api/invoices/sunmed/${invoiceId}/resend`);
  return response.data;
};

export const updateSunMedInvoiceStatus = async (invoiceId, status) => {
  const response = await api.patch(`/api/invoices/sunmed/${invoiceId}/status`, null, {
    params: { status }
  });
  return response.data;
};

export const deleteSunMedInvoice = async (invoiceId) => {
  const response = await api.delete(`/api/invoices/sunmed/${invoiceId}`);
  return response.data;
};

// PayPal payment helpers (v1 — sandbox/preview only)
export const createPaypalOrder = async (bookingId) => {
  const response = await api.post('/api/payments/paypal/create-order', { bookingId });
  return response.data; // { orderId, status }
};

export const capturePaypalOrder = async (orderId) => {
  const response = await api.post(`/api/payments/paypal/${orderId}/capture`);
  return response.data; // { status, captureId, amount, currency, alreadyPaid }
};



// =============================================
// Hey Taxi! Blog APIs (2026-06-10)
// =============================================

// Public — list/get published posts (no auth required)
export const listBlogPosts = async () => {
  const response = await api.get('/api/blog/posts');
  return response.data;
};

export const getBlogPostBySlug = async (slug) => {
  const response = await api.get(`/api/blog/posts/${slug}`);
  return response.data;
};

// Admin — CRUD
export const adminListBlogPosts = async () => {
  const response = await api.get('/api/blog/admin/posts');
  return response.data;
};

export const adminCreateBlogPost = async (postData) => {
  const response = await api.post('/api/blog/admin/posts', postData);
  return response.data;
};

export const adminUpdateBlogPost = async (postId, updates) => {
  const response = await api.patch(`/api/blog/admin/posts/${postId}`, updates);
  return response.data;
};

export const adminDeleteBlogPost = async (postId) => {
  const response = await api.delete(`/api/blog/admin/posts/${postId}`);
  return response.data;
};

export const listAdminPayments = async () => {
  const response = await api.get('/api/admin/payments');
  return response.data;
};
